<!-- =========================================================================================
    File Name: EmailSidebar.vue
    Description: Email Application Sidebar(Filter) (Inbox)
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
      Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->


<template>
  <div class="email__email-sidebar h-full">
    <div class="m-6 clearfix">
      <vs-button @click="activePrompt = true" class="bg-primary-gradient w-full" icon="icon-edit" icon-pack="feather">Compose</vs-button>
    </div>

    <!-- compose email -->
    <vs-prompt
      :active.sync="activePrompt"
      :is-valid="validateForm"
      @accept="sendMail"
      @cancel="clearFields"
      @close="clearFields"
      accept-text="Send"
      class="email-compose"
      title="New Message">
      <component :is="scrollbarTag" :key="$vs.rtl" :settings="settings" class="scroll-area p-4">
        <form @submit.prevent>
          <vs-input :color="validateForm ? 'success' : 'danger'" :danger="!validateForm && mailTo != ''" :success="validateForm" class="w-full mb-6" label-placeholder="To" name="mailTo" v-model="mailTo" v-validate="'required|email'" val-icon-danger="clear" val-icon-success="done"/>
          <vs-input class="w-full mb-6" label-placeholder="Subject" name="mailSubject" v-model="mailSubject"/>
          <vs-input class="w-full mb-6" label-placeholder="CC" name="mailCC" v-model="mailCC"/>
          <vs-input class="w-full mb-6" label-placeholder="BCC" name="mailBCC" v-model="mailBCC"/>
          <quill-editor :options="editorOption" v-model="mailMessage"/>
          <vs-upload :show-upload-button="false" class="compose-mail-attachment" multiple text="Attachments"/>
        </form>
      </component>
    </vs-prompt>

    <component :is="scrollbarTag" :key="$vs.rtl" :settings="settings" class="email-filter-scroll-area">
      <div class="px-6 pb-2 flex flex-col">

        <!-- inbox -->
        <router-link :class="{'text-primary': mailFilter == 'inbox'}" :to="`${baseUrl}/inbox`" class="flex justify-between items-center cursor-pointer" tag="span">
          <div class="flex items-center mb-2">
            <feather-icon :svgClasses="[{'text-primary stroke-current': mailFilter == 'inbox'}, 'h-6 w-6']" icon="MailIcon"></feather-icon>
            <span class="text-lg ml-3">Inbox</span>
          </div>
          <template v-if="emailMeta.unreadMails">
            <vs-chip class="number" color="primary" v-if="emailMeta.unreadMails.folder.inbox.length > 0">{{ emailMeta.unreadMails.folder.inbox.length }}</vs-chip>
          </template>
        </router-link>

        <!-- sent -->
        <router-link :class="{'text-primary': mailFilter == 'sent'}" :to="`${baseUrl}/sent`" class="flex items-center mt-4 mb-2 cursor-pointer" tag="span">
          <feather-icon :svgClasses="[{'text-primary stroke-current': mailFilter == 'sent'}, 'h-6 w-6']" icon="SendIcon"></feather-icon>
          <span class="text-lg ml-3">Sent</span>
        </router-link>

        <!-- draft -->
        <router-link :class="{'text-primary': mailFilter == 'draft'}" :to="`${baseUrl}/draft`" class="flex justify-between items-center mt-4 cursor-pointer" tag="span">
          <div class="flex items-center mb-2">
            <feather-icon :svgClasses="[{'text-primary stroke-current': mailFilter == 'draft'}, 'h-6 w-6']" icon="Edit2Icon"></feather-icon>
            <span class="text-lg ml-3">Draft</span>
          </div>
          <template v-if="emailMeta.draftMails">
            <vs-chip class="number" color="warning" v-if="emailMeta.draftMails.length > 0">{{ emailMeta.draftMails.length }}</vs-chip>
          </template>
        </router-link>

        <!-- starred -->
        <router-link :class="{'text-primary': mailFilter == 'starred'}" :to="`${baseUrl}/starred`" class="flex items-center mt-4 mb-2 cursor-pointer" tag="span">
          <feather-icon :svgClasses="[{'text-primary stroke-current': mailFilter == 'starred'}, 'h-6 w-6']" icon="StarIcon"></feather-icon>
          <span class="text-lg ml-3">Starred</span>
        </router-link>

        <!-- spam -->
        <router-link :class="{'text-primary': mailFilter == 'spam'}" :to="`${baseUrl}/spam`" class="flex items-center justify-between items-center mt-4 cursor-pointer" tag="span">
          <div class="flex items-center mb-2">
            <feather-icon :svgClasses="[{'text-primary stroke-current': mailFilter == 'spam'}, 'h-6 w-6']" icon="InfoIcon"></feather-icon>
            <span class="text-lg ml-3">Spam</span>
          </div>
          <template v-if="emailMeta.unreadMails">
            <vs-chip class="number" color="danger" v-if="emailMeta.unreadMails.folder.spam.length > 0">{{ emailMeta.unreadMails.folder.spam.length }}</vs-chip>
          </template>
        </router-link>

        <!-- trash -->
        <router-link :class="{'text-primary': mailFilter == 'trash'}" :to="`${baseUrl}/trash`" class="flex items-center mt-4 mb-2 cursor-pointer" tag="span">
          <feather-icon :svgClasses="[{'text-primary stroke-current': mailFilter == 'trash'}, 'h-6 w-6']" icon="TrashIcon"></feather-icon>
          <span class="text-lg ml-3">Trash</span>
        </router-link>
      </div>
      <vs-divider></vs-divider>
      <div class="email__labels px-6 py-4">
        <h5 class="mb-8">Labels</h5>
        <div class="email__lables-list">
          <router-link :key="index" :to="`${baseUrl}/${tag.value}`" class="email__label flex items-center mb-4 cursor-pointer" tag="span" v-for="(tag, index) in emailTags">
            <div :class="'border-2 border-solid border-' + tag.color" class="ml-1 h-3 w-3 rounded-full mr-4"></div>
            <span :class="{'text-primary': mailFilter == tag.value}" class="text-lg">{{ tag.text }}</span>
          </router-link>
        </div>
      </div>
    </component>
  </div>
</template>

<script>
  import 'quill/dist/quill.core.css'
  import 'quill/dist/quill.snow.css'
  import { quillEditor } from 'vue-quill-editor'
  import VuePerfectScrollbar from 'vue-perfect-scrollbar'

  export default {
    props:      {
      emailTags:  {
        type:     Array,
        required: true
      },
      mailFilter: {
        type:     String,
        required: true
      }
    },
    data ()
    {
      return {
        activePrompt: false,
        mailTo:       '',
        mailSubject:  '',
        mailCC:       '',
        mailBCC:      '',
        mailMessage:  '',
        editorOption: {
          modules:     {
            toolbar: [
              ['bold', 'italic', 'underline', 'strike', 'link', 'blockquote', 'code-block'],
              [{'header': 1}, {'header': 2}],
              [{'list': 'ordered'}, {'list': 'bullet'}],
              [{'font': []}]
            ]
          },
          placeholder: 'Message'
        },
        settings:     {
          maxScrollbarLength: 60,
          wheelSpeed:         0.30
        }
      }
    },
    computed:   {
      validateForm ()
      {
        return !this.errors.any() && this.mailTo !== ''
      },
      baseUrl ()
      {
        const path = this.$route.path
        return path.slice(0, path.lastIndexOf('/'))
      },
      emailMeta ()
      {
        return this.$store.state.email.meta
      },
      scrollbarTag () { return this.$store.getters.scrollbarTag }
    },
    methods:    {
      clearFields ()
      {
        this.$nextTick(() => {
          this.mailTo      = ''
          this.mailSubject = ''
          this.mailCC      = ''
          this.mailBCC     = ''
          this.mailMessage = ''
        })
      },
      sendMail ()
      {
        this.clearFields()
      }
    },
    components: {
      quillEditor,
      VuePerfectScrollbar
    }
  }

</script>

